import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Typography, TextField, Button, Box } from '@mui/material';
import { toast } from 'react-toastify';
import { verifyOtp } from '@/api/asset/admin/read';
import { SUCCESS } from '@/lib/common/name-space';

/**
 * 범용 OTP 인증 다이얼로그 컴포넌트
 *
 * SOLID 원칙 적용:
 * - Single Responsibility: OTP 인증 UI만 담당
 * - Open/Closed: 다양한 onSuccess 콜백으로 확장 가능
 * - Liskov Substitution: 다른 인증 방식으로 대체 가능
 * - Interface Segregation: 필요한 props만 정의
 * - Dependency Inversion: 구체적인 비즈니스 로직에 의존하지 않음
 */

interface OtpDialogProps {
  /** 다이얼로그 열림/닫힘 상태 */
  open: boolean;
  /** 다이얼로그 닫기 핸들러 */
  onClose: () => void;
  /** OTP 인증 성공 시 콜백 함수 */
  onSuccess: () => void;
  /** 다이얼로그 제목 (옵션) */
  title?: string;
  /** 설명 텍스트 (옵션) */
  description?: string;
  /** 로딩 상태 (옵션) */
  isLoading?: boolean;
}

export default function OtpDialog({
  open,
  onClose,
  onSuccess,
  title = 'OTP 인증',
  description = '보안을 위해 OTP 인증이 필요합니다.',
  isLoading = false,
}: OtpDialogProps) {
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleClose = () => {
    setOtpCode('');
    onClose();
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      toast.error('OTP 코드를 입력해주세요.');
      return;
    }

    setIsVerifying(true);
    try {
      const login = sessionStorage.getItem('loginId');
      const verifyResult = await verifyOtp({
        id: login || '',
        otpCode: otpCode,
      });

      if (verifyResult.status === SUCCESS) {
        setOtpCode('');
        onSuccess();
      } else {
        toast.error('OTP 인증에 실패했습니다.');
      }
    } catch (e) {
      console.log(e);
      toast.error('인증 처리 중 오류가 발생했습니다.');
      setOtpCode(''); // 오류 발생 시 입력된 OTP 코드 초기화
    } finally {
      setIsVerifying(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && otpCode.trim() && !isVerifying && !isLoading) {
      handleVerifyOtp();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>

          <TextField
            fullWidth
            label="OTP 코드"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
            }}
            sx={{ mb: 3 }}
            autoFocus
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handleClose} disabled={isVerifying || isLoading} fullWidth>
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleVerifyOtp}
              disabled={isVerifying || isLoading || !otpCode.trim()}
              fullWidth
            >
              {isVerifying ? '인증 중...' : '인증하기'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
